import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateMarginUSDCRequiredForTrade,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useVelocityStore, {
	DEFAULT_MARKET_CONFIG,
} from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import {
	computeIsolatedPositionDepositForTrade,
	getTotalIsolatedMarginShortfall,
	UIMarket,
	convertLeverageToMarginRatio,
} from '@velocity-exchange/common';
import { ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR } from 'src/constants/trade';
import { useEffectiveMarginMode } from 'src/hooks/marginMode/useEffectiveMarginMode';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

interface Params {
	/**
	 * Pulls from `tradeForm.baseSizeStringValue` if not provided.
	 */
	baseSizeString?: string;
	/**
	 * Defaults to `0` if not provided.
	 */
	marketIndex?: number;
	/**
	 * Pulls from `tradeForm.priceImpact.entryPrice` if not provided.
	 */
	estEntryPrice?: BN;
}
/**
 * Determines how much USDC is required for the trade.
 */
export const useCollateralValueRequiredForTrade = ({
	marketIndex = 0,
	baseSizeString: baseSizeStringFromProps,
	estEntryPrice: estEntryPriceFromProps,
}: Params = {}) => {
	const baseSizeStringFromStore = useVelocityStore(
		(s) => s.tradeForm.baseSizeStringValue
	);
	const baseSizeString = baseSizeStringFromProps ?? baseSizeStringFromStore;

	const estEntryPriceFromStore = useVelocityStore(
		(s) => s.tradeForm.priceImpact?.entryPrice
	);
	const estEntryPrice = estEntryPriceFromProps ?? estEntryPriceFromStore;

	const velocityClient = useVelocityStore((s) => s.velocityClient.client);

	const velocityClientIsReady = useVelocityClientIsReady();

	const currentUserAccount = useVelocityAccountStore((s) =>
		s.getCurrentUserAccount()
	);

	// Calculate display leverage for margin ratio (same logic as getMaxTradeSizeUSDCForPerp)
	const displayLeverage = useGetDisplayLeverageForMarket(marketIndex);
	const maxMarginRatio = convertLeverageToMarginRatio(displayLeverage);

	const uiMarket = UIMarket.createPerpMarket(marketIndex);
	const marginMode = useEffectiveMarginMode(uiMarket.key).mode;

	const baseSizeBn = useMemo(
		() => parseInputBigNumOrZero(baseSizeString, BASE_PRECISION_EXP).val,
		[baseSizeString]
	);

	const isolatedPositionDeposit = useMemo(() => {
		if (
			marginMode === 'isolated' &&
			velocityClient &&
			currentUserAccount?.client
		) {
			return (
				computeIsolatedPositionDepositForTrade({
					velocityClient: velocityClient,
					user: currentUserAccount.client,
					marketIndex:
						uiMarket?.marketIndex ?? DEFAULT_MARKET_CONFIG.marketIndex,
					baseAssetAmount: baseSizeBn,
					marginRatio: convertLeverageToMarginRatio(displayLeverage ?? 1) ?? 1,
					bufferDenominator: ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR,
					includeExistingShortfall: true,
				}) ?? undefined
			);
		}
	}, [baseSizeBn, uiMarket?.marketIndex, marginMode, displayLeverage]);

	// Total shortfall from other isolated positions (~6.67% buffer via ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR)
	const otherIsolatedShortfallsTotal = useMemo(() => {
		if (!currentUserAccount?.client) {
			return ZERO;
		}
		const rawShortfall = getTotalIsolatedMarginShortfall(
			currentUserAccount.client,
			uiMarket?.marketIndex
		);
		// Add buffer on top of shortfall (same as new margin; denominator 15 ≈ 6.67%)
		return rawShortfall.add(
			rawShortfall.div(new BN(ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR))
		);
	}, [currentUserAccount?.client, uiMarket?.marketIndex]);

	const collateralRequiredBigNum = useMemo(() => {
		if (!velocityClientIsReady || !velocityClient) {
			return ZERO_QUOTE_BIGNUM;
		}

		if (marginMode === 'isolated') {
			// For isolated mode, include the deposit + other shortfalls
			const baseDeposit = isolatedPositionDeposit ?? ZERO;
			const totalRequired = baseDeposit.add(otherIsolatedShortfallsTotal);
			return BigNum.from(totalRequired, QUOTE_PRECISION_EXP);
		}

		// For cross mode, include the margin required + any isolated shortfalls
		const collateralRequiredBn = calculateMarginUSDCRequiredForTrade(
			velocityClient,
			marketIndex,
			baseSizeBn,
			maxMarginRatio,
			estEntryPrice
		);

		const totalRequired = collateralRequiredBn.add(
			otherIsolatedShortfallsTotal
		);
		return BigNum.from(totalRequired, QUOTE_PRECISION_EXP);
	}, [
		baseSizeBn,
		velocityClient,
		estEntryPrice,
		velocityClientIsReady,
		marketIndex,
		maxMarginRatio,
		isolatedPositionDeposit,
		otherIsolatedShortfallsTotal,
	]);

	return collateralRequiredBigNum;
};
